import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Axios from 'axios';
import AppLayout from '../../components/AppLayout';
import CircularLoader from '../../components/CircularLoader/CircularLoader';
import { S, Icon, StatusBadge, formatDate, formatDateTime, formatMoney } from '../regureStyles';
import { docTypeLabel } from '../../utils/docTypeLabels';

// Portal-scoped Case detail.
//
// "Your Cases" listed case numbers styled as links that went nowhere: there
// was no /portal/my-claims/:id route and no screen behind it, so a
// policyholder could not open their own case at all. The backend surface
// already existed — GET /me/my-claims/:id enforces the portal link
// (claimant.email for a policyholder, brokered-policy check for a broker)
// and strips internal notes before responding.
//
// Read-only by construction. Portal users must never reach the back-office
// ClaimDetail screen, and nothing here writes. Internal financials — the
// reserve, assignee, audit trail, internal notes — are deliberately not
// rendered; the amount actually paid is, because that is the claimant's own
// money and the one figure they have a reason to check.
const MyClaimDetailScreen = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const userLogin = useSelector(state => state.userLogin);
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    if (userLogin?.userInfo?.status !== 'success') return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const response = await Axios.get(`${apiUrl}/me/my-claims/${id}`, { withCredentials: true });
        if (!cancelled) setClaim(response.data?.data || null);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || t('screens.portal.failedLoadClaims'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userLogin, apiUrl, id, t]);

  if (loading) return (
    <AppLayout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularLoader />
      </div>
    </AppLayout>
  );

  const Field = ({ label, value }) => (
    <div style={{ padding: '12px 0', borderBottom: `1px solid ${S.borderLight}` }}>
      <div style={{ fontSize: 11, color: S.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, color: S.textPrimary, fontWeight: 500 }}>{value || '—'}</div>
    </div>
  );

  const card = {
    background: '#fff', border: `1px solid ${S.borderLight}`,
    borderRadius: 14, padding: '20px 24px',
  };

  const documents = Array.isArray(claim?.documents) ? claim.documents : [];
  const notes = Array.isArray(claim?.notes) ? claim.notes : [];

  return (
    <AppLayout>
      <div style={{
        flex: 1,
        overflowY: 'auto',
        background: `linear-gradient(135deg, ${S.bg} 0%, #f8fafc 100%)`,
        padding: '32px 40px',
        minHeight: '100%',
      }}>
        <button
          onClick={() => history.push('/portal/my-claims')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: S.textMuted, fontSize: 13, fontWeight: 600, padding: 0, marginBottom: 16,
          }}
        >
          <Icon name="chevron-left" size={14} />
          {t('screens.portal.breadcrumbMyClaims')}
        </button>

        {error && (
          <div style={{
            background: 'rgba(220, 38, 38, 0.08)', color: '#dc2626',
            padding: '14px 18px', borderRadius: 10, marginBottom: 20,
            fontSize: 13, border: '1px solid rgba(220, 38, 38, 0.15)',
          }}>
            {error}
          </div>
        )}

        {claim && (
          <>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {claim.lineOfBusiness || '—'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: S.textPrimary, letterSpacing: '-0.5px' }}>
                  {claim.claimNumber || '—'}
                </div>
                <StatusBadge status={claim.status || 'reported'} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
              <div style={card}>
                <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary, marginBottom: 6 }}>
                  {t('screens.portal.caseSummary')}
                </div>
                <Field label={t('screens.portal.claimsColClaimNumber')} value={claim.claimNumber} />
                <Field label={t('screens.portal.claimsColStatus')} value={<StatusBadge status={claim.status || 'reported'} />} />
                <Field label={t('screens.portal.claimsColLossDate')} value={formatDate(claim.lossDate)} />
                <Field label={t('screens.portal.claimsColFiled')} value={formatDate(claim.reportedDate || claim.createdAt)} />
                <Field label={t('screens.portal.claimsColPolicy')} value={claim.policyNumber} />
              </div>

              <div style={card}>
                <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary, marginBottom: 6 }}>
                  {t('screens.portal.caseDetails')}
                </div>
                <Field label={t('screens.portal.claimsColDescription')} value={claim.lossDescription} />
                <Field label={t('screens.portal.lossLocation')} value={claim.lossLocation} />
                {/* Amount settled, in the record's own currency. The reserve is
                    an internal figure and is deliberately absent. */}
                <Field
                  label={t('screens.portal.amountPaid')}
                  value={claim.paidAmount != null ? formatMoney(claim.paidAmount, claim.currency) : '—'}
                />
              </div>
            </div>

            <div style={{ ...card, marginBottom: 20 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary, marginBottom: 14 }}>
                {t('screens.portal.breadcrumbMyDocuments')}
              </div>
              {documents.length === 0 ? (
                <div style={{ fontSize: 13, color: S.textMuted }}>{t('screens.portal.noDocumentsYet')}</div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  {documents.map(d => (
                    <div key={d._id || d.name} style={{ border: `1px solid ${S.borderLight}`, borderRadius: 10, padding: '12px 14px', minWidth: 210 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Icon name="file-text" size={14} color={S.burgundy} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: S.textPrimary }}>
                          {d.name || d.fileName || t('screens.portal.documentFallback')}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: S.textMuted, marginTop: 4 }}>
                        {docTypeLabel(d.documentType || d.type, i18n.language)}
                      </div>
                      <div style={{ fontSize: 11, color: S.textMuted, marginTop: 2 }}>
                        {formatDate(d.createdAt || d.uploadedAt)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {notes.length > 0 && (
              <div style={card}>
                <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary, marginBottom: 14 }}>
                  {t('screens.portal.caseUpdates')}
                </div>
                {notes.map((n, i) => (
                  <div key={n._id || i} style={{ padding: '10px 0', borderBottom: i < notes.length - 1 ? `1px solid ${S.borderLight}` : 'none' }}>
                    <div style={{ fontSize: 13, color: S.textPrimary }}>{n.text || n.body || ''}</div>
                    <div style={{ fontSize: 11, color: S.textMuted, marginTop: 3 }}>{formatDateTime(n.createdAt)}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
};

export default MyClaimDetailScreen;
